// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Vehicle } from '@interfaces/vehicle.interfaces';
import { takeWhile } from 'rxjs';
import { VehicleSearchModal } from 'src/app/modals/vehicle-search-modal/vehicle-search-modal.component';
import { Deal, VehicleType } from '../../interfaces/deal.interfaces';
import { AppEvent } from '../../interfaces/interfaces';
import { ErrorService } from '../../services/error.service';
import { showFormErrors } from '../../utils/form-utils';

@Component({
    selector: 'app-vehicle-edit',
    templateUrl: './vehicle-edit.component.html',
    styleUrls: ['./vehicle-edit.component.scss'],
})
export class VehicleEditComponent implements OnInit, OnDestroy {
    @Input() vehicle: Vehicle;
    @Input() deal: Deal;
    @Input() condensed = false;
    @Input() selected = false;
    @Input() search = false;
    @Input() type: 'jacket' | 'lead' = 'jacket';
    @Input() vehicleType: VehicleType = VehicleType.Vehicle;
    @Output() outEvent = new EventEmitter<AppEvent>();
    @Output() selected$ = new EventEmitter();
    private isComponentAlive = true;
    public vehicleGroup: FormGroup;
    public selectedVehicle: any;
    public carStatus: { value: string; displayValue: string }[] = [
        { value: 'new', displayValue: 'New' },
        { value: 'cpo', displayValue: 'CPO' },
        { value: 'used', displayValue: 'Used' },
    ];
    protected readonly VehicleType = VehicleType;

    constructor(
        private fb: FormBuilder,
        private readonly dialog: MatDialog
    ) {}

    get isVehicleManuallyEntered(): boolean {
        const manualVehicle = this.vehicleGroup.value;
        return (
            this.selectedVehicle === null ||
            manualVehicle.vin !== this.selectedVehicle.vin ||
            manualVehicle.stockNumber !== this.selectedVehicle.stockNumber ||
            manualVehicle.priceInternet !== this.selectedVehicle.vehicleExt.priceInternet ||
            manualVehicle.status !== (this.selectedVehicle.vehicleExt.status || '').toLowerCase() ||
            manualVehicle.year !== this.selectedVehicle.year ||
            manualVehicle.make !== this.selectedVehicle.make ||
            manualVehicle.model !== this.selectedVehicle.model ||
            manualVehicle.trim !== this.selectedVehicle.trim ||
            manualVehicle.bodyStyle !== this.selectedVehicle.bodyStyle ||
            manualVehicle.bodyType !== this.selectedVehicle.vehicleExt.bodyType ||
            manualVehicle.extColor !== this.selectedVehicle.vehicleExt.exteriorColor ||
            manualVehicle.intColor !== this.selectedVehicle.vehicleExt.interiorColor ||
            manualVehicle.mileage !== this.selectedVehicle.mileage
        );
    }

    vehicleSelected(vehicle: Vehicle) {
        this.selected$.emit(vehicle);
    }
    ngOnInit(): void {
        this.buildForm();
        this.setFormValues(this.vehicle);

        if (this.vehicle) {
            this.selectedVehicle = this.vehicle;
        }
    }

    private buildForm(): void {
        this.vehicleGroup = this.fb.group({
            vin: ['', Validators.compose([Validators.minLength(5), Validators.maxLength(18)])],
            stockNumber: [''],
            priceInternet: [null],
            status: [''],
            year: [null, Validators.compose([Validators.min(1900), Validators.max(3000)])],
            make: [''],
            model: [''],
            trim: [''],
            bodyType: [''],
            bodyStyle: [''],
            extColor: [''],
            intColor: [''],
            mileage: [null],
        });
    }

    openSearch() {
        const vehicle = this.dialog.open(VehicleSearchModal);

        vehicle
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((selectedVehicle) => {
                if (selectedVehicle) {
                    this.setFormValues(selectedVehicle, true);
                }
            });
    }

    closeModal(success: any = false): void {
        this.outEvent.emit(success);
    }

    private setFormValues(data: any, search = false): void {
        this.vehicleGroup.setValue({
            vin: data?.vin || '',
            stockNumber: data?.stockNumber || '',
            priceInternet: data?.vehicleExt?.priceInternet || null,
            status: (data?.vehicleExt?.status || '').toLowerCase(),
            year: data?.year || null,
            make: data?.make || '',
            model: data?.model || '',
            trim: data?.trim || '',
            bodyType: data?.vehicleExt?.bodyType || '',
            bodyStyle: data?.bodyStyle || '',
            extColor: data?.vehicleExt?.exteriorColor || '',
            intColor: data?.vehicleExt?.interiorColor || '',
            mileage: data?.mileage || null,
        });

        if (search) {
            this.selectedVehicle = {
                ...data,
                dealId: this.deal.dealId,
                dealerId: this.deal.dealerId,
            };
            this.vehicleGroup.updateValueAndValidity();
        }
    }

    public save(): void {
        if (this.vehicleGroup.status === 'INVALID') {
            showFormErrors(this.vehicleGroup.controls);
            return;
        }

        const vehicle: any = {
            dealId: this.deal.dealId,
            dealerId: this.deal.dealerId,
            vin: this.vehicleGroup.controls.vin.value,
            stockNumber: this.vehicleGroup.controls.stockNumber.value,
            vehicleExt: {
                priceInternet: this.vehicleGroup.controls.priceInternet.value,
                status: this.carStatus.find((status) => status.value === this.vehicleGroup.controls.status.value)?.displayValue,
                bodyType: this.vehicleGroup.controls.bodyType.value,
                exteriorColor: this.vehicleGroup.controls.extColor.value,
                interiorColor: this.vehicleGroup.controls.intColor.value,
            },
            year: this.vehicleGroup.controls.year.value || null,
            make: this.vehicleGroup.controls.make.value,
            model: this.vehicleGroup.controls.model.value,
            trim: this.vehicleGroup.controls.trim.value,
            bodyStyle: this.vehicleGroup.controls.bodyStyle.value,
            mileage: this.vehicleGroup.controls.mileage.value,
        };

        if (this.isVehicleManuallyEntered) {
            this.selectedVehicle = vehicle;
        }

        this.closeModal(this.selectedVehicle);
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
